import {
	Search,
	West,
} from "@mui/icons-material";
import {
	Alert,
	Box,
	Button,
	Grid,
	IconButton,
	InputBase,
	MenuItem,
	Paper,
	Snackbar,
	Switch,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
	UserListItem,
	UserRole,
	UserWarehouse,
} from "../../../@types/DRO/UserManagementResponse";
import { UpdateUserPayload } from "../../../@types/DTO/UserManagementBody";
import { UserManagementApi } from "../../../api/UserManagement/UserManagementApi";
import { useLoading } from "../../../views/Loader/LoadingContext";

interface EditUserFormValues {
	name: string;
	mobileNumber: string;
	roleId: string;
	warehouseId: string;
	active: boolean;
}

interface FormErrors {
	name?: string;
	mobileNumber?: string;
	roleId?: string;
	warehouseId?: string;
}

const initialForm: EditUserFormValues = {
	name: "",
	mobileNumber: "",
	roleId: "",
	warehouseId: "",
	active: true,
};

const formatRoleLabel = (role: string): string => {
	return role
		.replace(/^ROLE_/, "")
		.replace(/_/g, " ");
};

const EditUser: React.FC = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const { userId } = useParams();
	const isDetailView = Boolean(userId);
	const { setLoading } = useLoading();
	const selectedUserFromState = (location.state as { user?: UserListItem })?.user;
	const [roles, setRoles] = useState<UserRole[]>([]);
	const [warehouses, setWarehouses] = useState<UserWarehouse[]>([]);
	const [users, setUsers] = useState<UserListItem[]>([]);
	const [isUsersLoading, setIsUsersLoading] = useState(false);
	const [searchInput, setSearchInput] = useState("");
	const [searchTerm, setSearchTerm] = useState("");
	const [page, setPage] = useState(0);
	const pageSize = 5;
	const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
	const [formValues, setFormValues] = useState<EditUserFormValues>(initialForm);
	const [errors, setErrors] = useState<FormErrors>({});
	const [snackbar, setSnackbar] = useState({
		open: false,
		message: "",
		severity: "success" as "success" | "error",
	});

	useEffect(() => {
		if (isDetailView) {
			return;
		}

		const timer = window.setTimeout(() => {
			setSearchTerm(searchInput.trim());
		}, 400);

		return () => {
			window.clearTimeout(timer);
		};
	}, [isDetailView, searchInput]);

	useEffect(() => {
		if (isDetailView) {
			return;
		}

		setPage(0);
	}, [isDetailView, searchTerm]);

	useEffect(() => {
		if (isDetailView) {
			return;
		}

		const fetchUsers = async () => {
			setIsUsersLoading(true);
			try {
				const userResponse = await UserManagementApi.api.getUsers(searchTerm, page, pageSize);
				setUsers(userResponse);
			} catch (error: unknown) {
				console.error("Error loading users", error);
				setSnackbar({
					open: true,
					message: "Unable to load users",
					severity: "error",
				});
			}
			setIsUsersLoading(false);
		};

		fetchUsers();
	}, [isDetailView, page, pageSize, searchTerm, setLoading]);

	useEffect(() => {
		if (!isDetailView || !userId || !selectedUserFromState) {
			return;
		}

		const fetchEditData = async () => {
			setLoading(true);
			try {
				const [rolesResponse, warehousesResponse] = await Promise.all([
					UserManagementApi.api.getRoles(),
					UserManagementApi.api.getWarehouses(),
				]);

				setRoles(rolesResponse);
				setWarehouses(warehousesResponse);

				const matchedRole = rolesResponse.find(
					(role) => role.role === selectedUserFromState.roleName
				);
				const matchedWarehouse = warehousesResponse.find(
					(warehouse) => warehouse.name === selectedUserFromState.warehouseName
				);

				setSelectedUserId(selectedUserFromState.id);
				setFormValues({
					name: selectedUserFromState.name,
					mobileNumber: selectedUserFromState.mobile,
					roleId: matchedRole ? String(matchedRole.id) : "",
					warehouseId: matchedWarehouse ? String(matchedWarehouse.id) : "",
					active: selectedUserFromState.active,
				});
			} catch (error: unknown) {
				console.error("Error loading user management options", error);
				setSnackbar({
					open: true,
					message: "Unable to load roles and warehouses",
					severity: "error",
				});
				navigate("/user-management/edit", { replace: true });
			}
			setLoading(false);
		};

		fetchEditData();
	}, [isDetailView, navigate, setLoading, userId, selectedUserFromState]);

	const selectedRole = useMemo(() => {
		return roles.find((role) => role.id === Number(formValues.roleId));
	}, [roles, formValues.roleId]);

	const shouldShowWarehouse = [
		"ROLE_INVENTORY_MANAGER", 
		"ROLE_FRANCHISE_INVENTORY_MANAGER", 
		"ROLE_ERP_FRANCHISE_ADMIN"
	].includes(selectedRole?.role ?? "");

	const handleFieldChange = (
		field: keyof EditUserFormValues,
		value: string | boolean
	) => {
		setFormValues((previous) => ({
			...previous,
			[field]: value as never,
			...(field === "roleId" ? { warehouseId: "" } : {}),
		}));

		if (errors[field as keyof FormErrors]) {
			setErrors((previous) => ({ ...previous, [field]: undefined }));
		}
	};

	const validate = (): boolean => {
		const currentErrors: FormErrors = {};

		if (!formValues.name.trim()) {
			currentErrors.name = "Name is required";
		}

		if (!formValues.mobileNumber.trim()) {
			currentErrors.mobileNumber = "Mobile number is required";
		} else if (!/^[0-9]{10}$/.test(formValues.mobileNumber)) {
			currentErrors.mobileNumber = "Mobile number must be 10 digits";
		}

		if (!formValues.roleId) {
			currentErrors.roleId = "Role is required";
		}

		if (shouldShowWarehouse && !formValues.warehouseId) {
			currentErrors.warehouseId = "Warehouse is required for inventory manager";
		}

		setErrors(currentErrors);
		return Object.keys(currentErrors).length === 0;
	};

	const handleUpdateUser = async () => {
		if (!selectedUserId) {
			setSnackbar({
				open: true,
				message: "Please select a user from the table",
				severity: "error",
			});
			return;
		}

		if (!validate()) {
			return;
		}

		setLoading(true);
		try {
			const payload: UpdateUserPayload = {
				name: formValues.name.trim(),
				mobileNumber: formValues.mobileNumber,
				roleId: Number(formValues.roleId),
				active: formValues.active,
				...(shouldShowWarehouse ? { warehouseId: Number(formValues.warehouseId) } : {}),
			};

			await UserManagementApi.api.updateUser(selectedUserId, payload);

			setSnackbar({
				open: true,
				message: "User updated successfully",
				severity: "success",
			});
			setFormValues(initialForm);
			setErrors({});
			navigate("/user-management/edit");
		} catch (error: unknown) {
			console.error("Error updating user", error);
			const message =
				typeof error === "object" &&
				error !== null &&
				"response" in error &&
				typeof (error as { response?: { data?: { message?: string } } }).response?.data
					?.message === "string"
					? (error as { response?: { data?: { message?: string } } }).response?.data
							?.message ?? "Error updating user"
					: "Error updating user";

			setSnackbar({
				open: true,
				message,
				severity: "error",
			});
		}
		setLoading(false);
	};

	const handleCloseSnackbar = () => {
		setSnackbar((previous) => ({ ...previous, open: false }));
	};

	const handleViewUser = (user: UserListItem) => {
		navigate(`/user-management/edit/${user.id}`, { state: { user } });
	};

	return (
		<Box sx={{ width: "100%", p: { xs: 2, md: 4 } }}>
			<Box sx={{ mb: 3 }}>
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						gap: 2,
						mb: 1,
					}}>
					<Typography
						variant="h5"
						gutterBottom
						sx={{
							fontWeight: "600",
							color: "#1E1E1E",
							textAlign: "left",
							fontSize: "1.5rem",
							mb: 0,
						}}>
						Edit User
					</Typography>

					{!isDetailView && (
						<Button
							variant="contained"
							onClick={() => navigate("/user-management/create")}
							sx={{
								textTransform: "none",
								borderRadius: "32px",
								px: 3,
								py: 1,
								fontWeight: 600,
								backgroundColor: "#1E1E1E",
								"&:hover": { backgroundColor: "#000000" },
							}}>
							Create User
						</Button>
					)}
				</Box>
				<div className="flex items-center bg-gray-100 px-4 py-2 rounded-md space-x-2">
					<Link
						to="/home"
						className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline transition-colors">
						Dashboard
					</Link>
					<span className="text-sm text-gray-400">/</span>
					<Link
						to="/user-management/create"
						className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline transition-colors">
						User Management
					</Link>
					<span className="text-sm text-gray-400">/</span>
					<span className="text-sm font-medium text-gray-700">Edit User</span>
				</div>
			</Box>

			{!isDetailView && (
				<Paper
					sx={{
						width: "100%",
						padding: 3,
						marginTop: 2,
						borderRadius: "12px",
						boxShadow: "0px 8px 24px rgba(15, 23, 42, 0.08)",
					}}>
					<Paper
						elevation={0}
						sx={{
							display: "flex",
							alignItems: "center",
							flexWrap: "wrap",
							gap: 1,
							px: 2,
							py: 1,
							borderRadius: "999px",
							border: "1px solid #dbe2ea",
							background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
							mb: 3,
						}}>
						<Search sx={{ color: "#64748b" }} />
						<InputBase
							value={searchInput}
							onChange={(event) => setSearchInput(event.target.value)}
							placeholder="Search users by name or email"
							sx={{ flex: 1, fontSize: "0.95rem" }}
						/>
						{searchInput && (
							<IconButton size="small" onClick={() => setSearchInput("")}>
								<Typography sx={{ fontSize: "0.8rem", color: "#64748b" }}>Clear</Typography>
							</IconButton>
						)}
					</Paper>

					<TableContainer sx={{ width: "100%", overflowX: "auto" }}>
						<Table size="medium" sx={{ minWidth: 950 }}>
							<TableHead
								sx={{
									"& .MuiTableCell-root": {
										fontWeight: 700,
										color: "#0f172a",
										backgroundColor: "#f8fafc",
										borderBottom: "1px solid #dbe2ea",
									},
								}}>
								<TableRow>
									<TableCell sx={{ whiteSpace: "nowrap" }}>User ID</TableCell>
									<TableCell sx={{ whiteSpace: "nowrap" }}>Name</TableCell>
									<TableCell sx={{ whiteSpace: "nowrap" }}>Email</TableCell>
									<TableCell sx={{ whiteSpace: "nowrap" }}>Mobile</TableCell>
									<TableCell sx={{ whiteSpace: "nowrap" }}>Role</TableCell>
									<TableCell sx={{ whiteSpace: "nowrap" }}>Status</TableCell>
									<TableCell sx={{ whiteSpace: "nowrap" }}>Warehouse</TableCell>
									<TableCell sx={{ whiteSpace: "nowrap" }}>Action</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{isUsersLoading ? (
									<TableRow>
										<TableCell colSpan={8} align="center">
											Loading users...
										</TableCell>
									</TableRow>
								) : users.length === 0 ? (
									<TableRow>
										<TableCell colSpan={8} align="center">
											No users found
										</TableCell>
									</TableRow>
								) : (
									users.map((user) => (
										<TableRow key={user.id}>
											<TableCell sx={{ whiteSpace: "nowrap" }}>{user.id}</TableCell>
											<TableCell sx={{ whiteSpace: "nowrap" }}>{user.name}</TableCell>
											<TableCell>{user.email}</TableCell>
											<TableCell sx={{ whiteSpace: "nowrap" }}>{user.mobile}</TableCell>
											<TableCell sx={{ whiteSpace: "nowrap" }}>
												{formatRoleLabel(user.roleName)}
											</TableCell>
											<TableCell sx={{ whiteSpace: "nowrap" }}>
												<Typography
													sx={{
														fontWeight: 600,
														color: user.active ? "#15803d" : "#dc2626",
													}}>
													{user.active ? "Active" : "Inactive"}
												</Typography>
											</TableCell>
											<TableCell>{user.warehouseName ?? "-"}</TableCell>
											<TableCell sx={{ whiteSpace: "nowrap" }}>
												<Button
													variant="contained"
													size="small"
													onClick={() => handleViewUser(user)}
													sx={{ textTransform: "none" }}>
													View
												</Button>
											</TableCell>
										</TableRow>
									))
								)}
							</TableBody>
						</Table>
					</TableContainer>

					<Box
						sx={{
							display: "flex",
							justifyContent: "flex-end",
							gap: 1,
							mt: 2,
							flexWrap: "wrap",
						}}>
						<Button
							variant="outlined"
							onClick={() => setPage((prev) => Math.max(prev - 1, 0))}
							disabled={page === 0}
							sx={{ textTransform: "none" }}>
							Previous
						</Button>
						<Typography sx={{ alignSelf: "center" }}>Page {page + 1}</Typography>
						<Button
							variant="outlined"
							onClick={() => setPage((prev) => prev + 1)}
							disabled={users.length < pageSize}
							sx={{ textTransform: "none" }}>
							Next
						</Button>
					</Box>
				</Paper>
			)}

			{isDetailView && selectedUserId && (
				<Paper
					sx={{
						padding: 3,
						marginTop: 2,
						borderRadius: "8px",
						boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
					}}>
					<Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
						<Typography sx={{ fontWeight: 600, textAlign: "left" }}>
						Editing User ID: {selectedUserId}
						</Typography>
						<Button
							startIcon={<West />}
							onClick={() => navigate("/user-management/edit")}
							sx={{ textTransform: "none" }}>
							Back to list
						</Button>
					</Box>
				<Grid container spacing={3} sx={{ textAlign: "left" }}>
					<Grid item xs={12} sm={4}>
						<TextField
							label="Name"
							fullWidth
							variant="standard"
							value={formValues.name}
							onChange={(event) => handleFieldChange("name", event.target.value)}
							error={!!errors.name}
							helperText={errors.name}
						/>
					</Grid>

					<Grid item xs={12} sm={4}>
						<TextField
							label="Mobile Number"
							fullWidth
							variant="standard"
							value={formValues.mobileNumber}
							onChange={(event) => {
								if (/^\d{0,10}$/.test(event.target.value)) {
									handleFieldChange("mobileNumber", event.target.value);
								}
							}}
							error={!!errors.mobileNumber}
							helperText={errors.mobileNumber}
							inputProps={{ maxLength: 10 }}
						/>
					</Grid>

					<Grid item xs={12} sm={4}>
						<TextField
							select
							label="Role"
							fullWidth
							variant="standard"
							value={formValues.roleId}
							onChange={(event) => handleFieldChange("roleId", event.target.value)}
							error={!!errors.roleId}
							helperText={errors.roleId}>
							{roles.map((role) => (
								<MenuItem key={role.id} value={String(role.id)}>
									{formatRoleLabel(role.role)}
								</MenuItem>
							))}
						</TextField>
					</Grid>

					{shouldShowWarehouse && (
						<Grid item xs={12} sm={4}>
							<TextField
								select
								label="Warehouse"
								fullWidth
								variant="standard"
								value={formValues.warehouseId}
								onChange={(event) =>
									handleFieldChange("warehouseId", event.target.value)
								}
								error={!!errors.warehouseId}
								helperText={errors.warehouseId}>
								{warehouses.map((warehouse) => (
									<MenuItem key={warehouse.id} value={String(warehouse.id)}>
										{warehouse.name}
									</MenuItem>
								))}
							</TextField>
						</Grid>
					)}

					<Grid item xs={12} sm={4}>
						<Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
							Status
						</Typography>
						<Box sx={{ display: "flex", alignItems: "center", minHeight: 40 }}>
							<Switch
								checked={formValues.active}
								onChange={(event) =>
									handleFieldChange("active", event.target.checked)
								}
							/>
							<Typography variant="body2">
								{formValues.active ? "Active" : "Inactive"}
							</Typography>
						</Box>
					</Grid>
				</Grid>
				</Paper>
			)}

			{isDetailView && selectedUserId && (
				<Box sx={{ marginTop: 4, textAlign: "right" }}>
					<Button
						variant="contained"
						onClick={handleUpdateUser}
						color="primary"
						sx={{
							padding: "10px 20px",
							borderRadius: "32px",
							backgroundColor: "#1E1E1E",
							textTransform: "none",
							fontWeight: "500",
							"&:hover": { backgroundColor: "#000000" },
						}}>
						Update User
					</Button>
				</Box>
			)}

			<Snackbar
				open={snackbar.open}
				autoHideDuration={2500}
				onClose={handleCloseSnackbar}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
				<Alert
					onClose={handleCloseSnackbar}
					severity={snackbar.severity}
					sx={{ width: "100%" }}>
					{snackbar.message}
				</Alert>
			</Snackbar>
		</Box>
	);
};

export default EditUser;
